/* global GeoJSON */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GeoJSONProperties } from '../hooks/useDealershipList';
import { PlaceResultType } from '../types/placeResultLocation';
import { getAvailableDealers } from '../utils/getAvailableDealers';
import { LsInterface } from '../hooks/useLocationLocalStorage';
import { SelectedUserLocation, Coordinates } from '../types/locations';
import { locationSelectAnalyticsEvent, radiusSliderEvent } from '../utils/analyticsEventListeners';
import { MARKET } from '../constants/market';

interface dealersListState {
  allDealersList: GeoJSON.FeatureCollection<GeoJSON.Point, GeoJSONProperties> | any;
  infoDealer: GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties> | any;
  dealersWithinRadius: GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties>[];
  confirmedDealersWithinRadius: GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties>[];
  centerLocation: PlaceResultType | any;
  confirmedCenterLocation: PlaceResultType | any;
  isEdit: boolean;
  radiusIsLoading: boolean;
  radius: number;
  confirmedRadius: number;
  selectedDealersIds: string[];
  confirmedSelectedDealerIds: string[];
}

const initialState: dealersListState = {
  allDealersList: {},
  infoDealer: {},
  centerLocation: undefined,
  confirmedCenterLocation: undefined,
  isEdit: false,
  radiusIsLoading: false,
  radius: MARKET === 'CAN' ? 150 : 50,
  confirmedRadius: MARKET === 'CAN' ? 150 : 50,
  dealersWithinRadius: [],
  confirmedDealersWithinRadius: [],
  selectedDealersIds: [],
  confirmedSelectedDealerIds: [],
};

const dealersListSlice = createSlice({
  name: 'dealersList',
  initialState,
  reducers: {
    setDealers: (_state: dealersListState, action: PayloadAction<dealersListState>) => {
      return { ..._state, allDealersList: action.payload };
    },
    setInfoDealer: (
      _state: dealersListState,
      action: PayloadAction<GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties>>,
    ) => {
      return { ..._state, infoDealer: action.payload };
    },
    clearInfoDetail: (_state: dealersListState) => {
      return { ..._state, infoDealer: {} };
    },
    setUserSelectedLocation: (
      _state: dealersListState,
      action: PayloadAction<{ place: PlaceResultType; isGeoLocation?: boolean }>,
    ) => {
      const centerLocation = action.payload.place;
      const { radius } = _state;
      const { allDealersList } = _state;
      const dealersWithinRadius = getAvailableDealers({ allDealersList, centerLocation, radius });

      const userCoordinates: Coordinates = {
        latitude: centerLocation.geometry.location.lat as number,
        longitude: centerLocation.geometry.location.lng as number,
      };

      const userSelectedLocation: SelectedUserLocation = {
        feature: centerLocation,
        userCoordinates,
      };
      locationSelectAnalyticsEvent(userSelectedLocation, action.payload.isGeoLocation);

      return {
        ..._state,
        centerLocation: action.payload.place,
        isEdit: true,
        dealersWithinRadius,
        selectedDealersIds: [],
      };
    },
    setToggleEdit: (_state: dealersListState) => {
      return { ..._state, isEdit: !_state.isEdit };
    },
    setToggleRadiusLoading: (_state: dealersListState, action: PayloadAction<boolean>) => {
      return { ..._state, radiusIsLoading: action.payload };
    },
    setRadius: (_state: dealersListState, action: PayloadAction<number>) => {
      const radius = action.payload;
      const { centerLocation } = _state;
      const { allDealersList } = _state;
      const dealersWithinRadius = getAvailableDealers({ allDealersList, centerLocation, radius });

      // Analytics Event
      radiusSliderEvent(radius.toString(), dealersWithinRadius.length, centerLocation);

      return {
        ..._state,
        radius: action.payload,
        dealersWithinRadius,
        selectedDealersIds: [],
        radiusIsLoading: false,
      };
    },
    clearSelectedDealersIds: (_state: dealersListState) => {
      return { ..._state, selectedDealersIds: [] };
    },
    toggleSelectedDealerId: (_state: dealersListState, action: PayloadAction<string>) => {
      const currentSelectedDealers = _state.selectedDealersIds;
      const selectedDealerId = action.payload;
      let newSelectedDealersIds: string[];
      if (currentSelectedDealers.includes(selectedDealerId)) {
        newSelectedDealersIds = currentSelectedDealers.filter(
          (dealerId) => dealerId !== selectedDealerId,
        );
      } else {
        newSelectedDealersIds = [...currentSelectedDealers, selectedDealerId];
      }

      return { ..._state, selectedDealersIds: newSelectedDealersIds };
    },

    // From Local Storage to Redux Store
    setUserAndRadiusLocation: (_state: dealersListState, action: PayloadAction<LsInterface>) => {
      const { allDealersList } = _state;
      const {
        selectedRadius,
        selectedUserLocation,
        invalidDataFromLs,
        selectedDealershipList,
        selectedSearchMode,
      } = action.payload;

      const selectedDealersIds: string[] =
        selectedSearchMode === 'BY_DEALERS' && selectedDealershipList.length
          ? selectedDealershipList.map((dealer) => dealer.kvpsSyncId)
          : [];
      const centerLocation: PlaceResultType = (selectedUserLocation as SelectedUserLocation)
        .feature;
      const dealersWithinRadius = getAvailableDealers({
        allDealersList,
        centerLocation,
        radius: selectedRadius,
      });

      if (invalidDataFromLs !== undefined && invalidDataFromLs) return { ..._state };

      return {
        ..._state,
        centerLocation,
        radius: selectedRadius,
        confirmedRadius: selectedRadius,
        confirmedCenterLocation: centerLocation,
        confirmedSelectedDealerIds: selectedDealersIds,
        dealersWithinRadius,
        confirmedDealersWithinRadius: dealersWithinRadius,
        selectedDealersIds,
        isEdit: true,
      };
    },
    setConfirmedFilters: (_state: dealersListState) => {
      const { radius } = _state;
      const { selectedDealersIds } = _state;
      const { dealersWithinRadius } = _state;
      const centerLocation = _state.centerLocation as PlaceResultType;

      return {
        ..._state,
        confirmedRadius: radius,
        confirmedCenterLocation: centerLocation,
        confirmedSelectedDealerIds: selectedDealersIds,
        confirmedDealersWithinRadius: dealersWithinRadius,
        isEdit: true,
      };
    },
    clearTempFilters: (_state: dealersListState) => {
      const { confirmedRadius } = _state;
      const { confirmedSelectedDealerIds } = _state;
      const { confirmedDealersWithinRadius } = _state;
      const confirmedCenterLocation = _state.confirmedCenterLocation as PlaceResultType;

      return {
        ..._state,
        radius: confirmedRadius,
        isEdit: !!confirmedCenterLocation,
        selectedDealersIds: confirmedSelectedDealerIds,
        centerLocation: confirmedCenterLocation,
        dealersWithinRadius: confirmedDealersWithinRadius,
      };
    },
    setPartner: (
      _state: dealersListState,
      action: PayloadAction<GeoJSON.Feature<GeoJSON.Point, GeoJSONProperties>>,
    ) => {
      const selectedPartner = action.payload;
      const selectedDealerId = action.payload.properties.kvpsSyncId;
      const userLocationCenter: PlaceResultType = {
        name: selectedPartner?.properties.name,
        formattedAddress: selectedPartner?.properties.name,
        region: selectedPartner?.properties.address.region,
        postalCode: selectedPartner?.properties.address.zipCode,
        city: selectedPartner?.properties.address.city,
        geometry: {
          location: {
            lat: selectedPartner?.properties.address.latitude,
            lng: selectedPartner?.properties.address.longitude,
          },
        },
      };

      const { allDealersList } = _state;

      const radius = MARKET === 'CAN' ? 150 : 50;

      const dealersWithinRadius = getAvailableDealers({
        allDealersList,
        centerLocation: userLocationCenter,
        radius,
      });

      return {
        ..._state,
        centerLocation: userLocationCenter,
        confirmedCenterLocation: userLocationCenter,
        radius,
        confirmedRadius: radius,
        selectedDealersIds: [selectedDealerId],
        confirmedSelectedDealerIds: [selectedDealerId],
        dealersWithinRadius,
        confirmedDealersWithinRadius: dealersWithinRadius,
        isEdit: true,
      };
    },
  },
});

export const selectDealersList = (state: any): dealersListState => state.dealersList;

export const {
  setDealers,
  setInfoDealer,
  clearInfoDetail,
  setToggleEdit,
  setUserSelectedLocation,
  setRadius,
  clearSelectedDealersIds,
  toggleSelectedDealerId,
  setUserAndRadiusLocation,
  setConfirmedFilters,
  clearTempFilters,
  setToggleRadiusLoading,
  setPartner,
} = dealersListSlice.actions;
export default dealersListSlice.reducer;
